import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useAuth } from './hooks'
import { reportAuthEvent } from './authTelemetry'

// Breaking the sign-in loop.
//
// `getAccessTokenSilently` rejecting does not surface as an Auth0 `error`: the
// SDK's own `finally` dispatches GET_ACCESS_TOKEN_COMPLETE with `getUser()`
// returning undefined, so the app simply becomes unauthenticated and every
// RequireRole on the page sends the member here. Auth0 still holds the SSO
// session, so the redirect this page fires is answered by an immediate 302 back
// — and straight into the same failing token call. A brand-new member watched
// that circuit run eight times before a token call happened to succeed.
//
// So the automatic redirect is rate limited per tab. One is the normal case;
// a second inside the cooldown means the round trip is not fixing anything, and
// the member gets a button instead of another flicker.
const AUTO_LOGIN_KEY = 'sc_auto_login_at'
const AUTO_LOGIN_COOLDOWN_MS = 10_000

/**
 * True when an automatic redirect is worth firing: we have not already fired
 * one for this tab in the last ten seconds. Reading sessionStorage can throw in
 * a locked down webview — treat that as "no record" rather than as a reason to
 * strand someone on a screen they cannot get past.
 */
function shouldAutoLogin(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(AUTO_LOGIN_KEY) ?? 0)
    return !Number.isFinite(last) || Date.now() - last >= AUTO_LOGIN_COOLDOWN_MS
  } catch {
    return true
  }
}

function markAutoLogin(): void {
  try {
    window.sessionStorage.setItem(AUTO_LOGIN_KEY, String(Date.now()))
  } catch {
    // Losing the guard is survivable; failing to sign in is not.
  }
}

/**
 * Send the browser to Auth0.
 *
 * appState.returnTo is how the invite token survives the redirect — never drop
 * it, on either path. `auth_return_to` is the fallback the provider reads when
 * appState does not come back. The catch only stops a failed redirect (offline,
 * popup blocked) becoming an unhandled rejection; the page renders nothing
 * while it is in flight, so there is no state to unwind. Wrapped in
 * Promise.resolve so a stubbed loginWithRedirect that returns nothing is safe.
 */
function beginLogin(
  loginWithRedirect: ReturnType<typeof useAuth>['loginWithRedirect'],
  returnTo: string,
  { showLoginForm = false }: { showLoginForm?: boolean } = {},
): void {
  markAutoLogin()
  try {
    window.sessionStorage.setItem('auth_return_to', returnTo)
  } catch {
    // appState carries the same value; this is only the fallback.
  }
  void Promise.resolve(
    loginWithRedirect({
      appState: { returnTo },
      ...(showLoginForm ? { authorizationParams: { prompt: 'login' } } : null),
    }),
  ).catch(() => {})
}

function clearAutoLogin(): void {
  try {
    window.sessionStorage.removeItem(AUTO_LOGIN_KEY)
  } catch {
    // Nothing recorded, nothing to clear.
  }
}

export function LoginPage({ errorMessage }: { errorMessage?: string }) {
  const { isAuthenticated, isLoading, loginWithRedirect, error, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo ?? '/'
  const [loopBroken, setLoopBroken] = useState(false)

  // A plain retry is answered by Auth0's SSO session in well under a second,
  // lands straight back in the same failing token call, and finds the
  // cooldown it just set, so the member sees this screen again and reads it
  // as a button that does nothing. Asking for the login form makes the retry
  // a real sign-in they can see.
  const retry = () => {
    setLoopBroken(false)
    beginLogin(loginWithRedirect, returnTo, { showLoginForm: true })
  }

  // Signing out ends the Auth0 session, so the next redirect is a fresh
  // sign-in, not a loop. Left in place, the cooldown from a few seconds ago
  // would put the member back on this screen instead of the login form.
  const signOut = () => {
    clearAutoLogin()
    void logout({ logoutParams: { returnTo: window.location.origin } })
  }

  useEffect(() => {
    if (isLoading || error) return
    if (isAuthenticated) {
      // Deliberately without clearing the cooldown. Every turn of the loop
      // passes through an authenticated render, so clearing it here would reset
      // the guard on exactly the case it exists to catch.
      navigate(returnTo, { replace: true })
      return
    }
    if (!shouldAutoLogin()) {
      reportAuthEvent('auth.login_loop_broken', { returnTo })
      setLoopBroken(true)
      return
    }
    beginLogin(loginWithRedirect, returnTo)
  }, [isAuthenticated, isLoading, error, loginWithRedirect, navigate, returnTo])

  if (error) {
    // Auth0 denials (e.g. the duplicate-email guard) put the human-readable text
    // in the OAuthError's error_description; on redirect it also stays in the URL
    // query. Prefer an app-provided message, then either of those, then a generic.
    const denialMessage =
      errorMessage ??
      (error as { error_description?: string }).error_description ??
      new URLSearchParams(window.location.search).get('error_description') ??
      t('auth.noPermissionApp')
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="max-w-sm text-center">
          <h2 className="text-xl font-bold">{t('auth.accessDeniedShort')}</h2>
          <p className="mt-2 text-sm text-foreground/60">{denialMessage}</p>
          <button
            className="mt-4 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground transition-opacity hover:opacity-80"
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          >
            {t('common.signOut')}
          </button>
        </div>
      </div>
    )
  }

  if (loopBroken) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="max-w-sm text-center">
          <h2 className="text-xl font-bold">{t('auth.sessionLoopTitle')}</h2>
          <p className="mt-2 text-sm text-foreground/60">{t('auth.sessionLoopDetail')}</p>
          <div className="mt-4 flex items-center justify-center gap-2">
            <button
              className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground transition-opacity hover:opacity-80"
              onClick={retry}
            >
              {t('auth.tryAgain')}
            </button>
            <button
              className="rounded-md px-4 py-2 text-sm text-foreground/60 transition-opacity hover:opacity-80"
              onClick={signOut}
            >
              {t('common.signOut')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return null
}
